if (!customElements.get('request-form')) {
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
  const ALLOWED_EXTENSIONS = /\.(jpe?g|png|pdf)$/i;
  const FILE_PROPERTY = '_request_form_file';
  const PENDING_LINE_KEY = 'request-form:pending-line';
  const SUBMITTED_FORM_KEY = 'request-form:submitted';
  const POSTED_PARAM = 'contact_posted';
  const CAPTCHA_OPT_OUT = 'data-nocaptcha';
  const CAPTCHA_TIMEOUT = 8000;
  const DRAG_EVENTS = ['dragenter', 'dragover', 'dragleave'];

  // Le stockage de session peut être refusé par le navigateur (navigation privée stricte) :
  // une valeur qui n'a pas pu être écrite se lit alors comme absente.
  const sessionValue = (key) => ({
    read() {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    write(value) {
      try {
        if (value) sessionStorage.setItem(key, value);
        else sessionStorage.removeItem(key);
      } catch {
        return;
      }
    },
  });
  const submittedForm = sessionValue(SUBMITTED_FORM_KEY);

  // Plusieurs retraits peuvent courir en même temps (fichier remplacé, deux formulaires sur la page) :
  // chacun n'inscrit et n'efface que sa propre ligne.
  const pendingLines = {
    store: sessionValue(PENDING_LINE_KEY),
    read() {
      try {
        const keys = JSON.parse(this.store.read());
        return Array.isArray(keys) ? keys : [];
      } catch {
        return [];
      }
    },
    write(keys) {
      this.store.write(keys.length ? JSON.stringify(keys) : null);
    },
    add(key) {
      this.write([...this.read().filter((pending) => pending !== key), key]);
    },
    delete(key) {
      this.write(this.read().filter((pending) => pending !== key));
    },
  };

  // Lu une seule fois pour la page : plusieurs formulaires s'y comparent, aucun ne doit l'effacer pour les autres.
  const reloadedFormId = submittedForm.read();
  submittedForm.write(null);

  class RequestForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.body = this.querySelector('[data-request-form-body]');
      this.success = this.querySelector('[data-request-form-success]');
      this.status = this.querySelector('[data-request-form-status]');
      this.submitButton = this.querySelector('[type="submit"]');
      this.fileInput = this.querySelector('[data-request-form-file]');
      this.fileUrlInput = this.querySelector('[data-request-form-file-url]');

      this.onSubmit = this.onSubmit.bind(this);
      // En phase de capture, donc avant les écouteurs que la protection anti-spam de Shopify pose sur le formulaire :
      // l'envoi passe par ce composant et par lui seul.
      this.addEventListener('submit', this.onSubmit, true);
      this.hookSubmit();

      if (this.fileInput) {
        this.dropzone = this.querySelector('[data-request-form-dropzone]');
        this.fileSummary = this.querySelector('[data-request-form-file-summary]');
        this.fileName = this.querySelector('[data-request-form-file-name]');
        this.removeButton = this.querySelector('[data-request-form-file-remove]');

        this.onFileChange = this.onFileChange.bind(this);
        this.onDrag = this.onDrag.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.clearFile = this.clearFile.bind(this);
        this.fileInput.addEventListener('change', this.onFileChange);
        this.removeButton.addEventListener('click', this.clearFile);
        DRAG_EVENTS.forEach((type) => this.dropzone.addEventListener(type, this.onDrag));
        this.dropzone.addEventListener('drop', this.onDrop);

        pendingLines.read().forEach((key) => this.removeLine(key));
      }

      this.handleReload();
    }

    // La protection anti-spam de Shopify enveloppe la méthode submit du formulaire : elle obtient son jeton,
    // le pose dans le formulaire, puis appelle la méthode qu'elle y a trouvée au moment de s'y lier.
    // Le formulaire lui reste fermé tant que l'envoi sans rechargement n'a pas pris cette place.
    hookSubmit() {
      if (!this.form.hasAttribute(CAPTCHA_OPT_OUT)) return;
      this.form.submit = () => this.send();
      this.form.removeAttribute(CAPTCHA_OPT_OUT);
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.onSubmit, true);
      clearTimeout(this.captchaTimer);
      if (!this.fileInput) return;
      this.fileInput.removeEventListener('change', this.onFileChange);
      this.removeButton.removeEventListener('click', this.clearFile);
      DRAG_EVENTS.forEach((type) => this.dropzone.removeEventListener(type, this.onDrag));
      this.dropzone.removeEventListener('drop', this.onDrop);
    }

    // Après un envoi par rechargement, Shopify lève le succès sur tous les formulaires de contact de la page.
    // L'ancre de l'action désigne celui qui a été envoyé, mais elle se perd quand l'envoi passe par le captcha :
    // l'identifiant noté à l'envoi fait alors foi.
    handleReload() {
      if (this.success.hidden) return;
      const own = reloadedFormId ? reloadedFormId === this.form.id : window.location.hash === `#${this.form.id}`;
      if (own) this.showSuccess();
      else this.success.hidden = true;
    }

    onFileChange() {
      this.fileUrlInput.value = '';
      const file = this.fileInput.files[0];
      this.fileName.textContent = file ? file.name : '';
      this.fileSummary.hidden = !file;
      const error = this.validate(file);
      this.setStatus(error, error ? 'error' : '');
      // Le fichier part dès qu'il est choisi : la demande n'a plus à l'attendre au moment de l'envoi.
      this.transfer = file && !error ? this.upload(file).catch(() => '') : null;
    }

    clearFile() {
      this.fileInput.value = '';
      this.onFileChange();
    }

    // Sans preventDefault sur le survol, le navigateur ouvre le fichier déposé au lieu de le remettre à la page.
    // Un dragleave remonte aussi des enfants de la zone : seul celui qui en sort vraiment éteint le survol.
    onDrag(event) {
      event.preventDefault();
      if (event.type === 'dragleave' && this.dropzone.contains(event.relatedTarget)) return;
      this.dropzone.toggleAttribute('data-dragging', event.type !== 'dragleave');
    }

    onDrop(event) {
      event.preventDefault();
      this.dropzone.removeAttribute('data-dragging');
      const { files } = event.dataTransfer;
      if (this.busy || !files.length) return;
      const transfer = new DataTransfer();
      transfer.items.add(files[0]);
      this.fileInput.files = transfer.files;
      this.onFileChange();
      if (files.length > 1 && !this.status.textContent) this.setStatus(this.dataset.singleFile, '');
    }

    validate(file) {
      if (!file) return '';
      // Certains sélecteurs de fichiers mobiles ne renseignent pas le type : l'extension fait alors foi.
      const accepted = file.type ? ALLOWED_TYPES.includes(file.type) : ALLOWED_EXTENSIONS.test(file.name);
      if (!accepted) return this.dataset.errorType;
      if (file.size > Number(this.dataset.maxBytes)) return this.dataset.errorSize;
      return '';
    }

    onSubmit(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy) return;

      const error = this.validate(this.fileInput?.files[0]);
      if (error) {
        this.setStatus(error, 'error');
        this.fileInput.focus();
        return;
      }

      this.setStatus(this.dataset.sending, 'busy');
      submittedForm.write(this.form.id);
      this.protect();
    }

    // Le formulaire reste utilisable tant que la protection anti-spam a la main : si le visiteur ferme son défi,
    // rien ne le signale. Le délai ne couvre que son chargement : si elle ne s'est pas liée au formulaire à temps
    // (script bloqué), la demande part sans elle et Shopify décide de la suite. Une fois liée, le défi n'a pas de délai.
    protect() {
      const captcha = window.Shopify?.captcha;
      if (!captcha) {
        this.send();
        return;
      }
      clearTimeout(this.captchaTimer);
      this.captchaTimer = setTimeout(() => this.send(), CAPTCHA_TIMEOUT);
      captcha.protect(this.form, () => {
        clearTimeout(this.captchaTimer);
        this.form.submit();
      });
    }

    async send() {
      if (this.busy || this.body.hidden) return;
      // Un fichier refusé, choisi pendant l'attente du jeton, a déjà son message : la demande ne part pas sans lui.
      if (this.fileInput?.files[0] && !this.transfer) return;

      this.setBusy(true);
      if (this.transfer) {
        this.setStatus(this.dataset.uploading, 'busy');
        this.fileUrlInput.value = await this.transfer;
        if (!this.fileUrlInput.value) {
          this.setBusy(false);
          this.clearFile();
          this.setStatus(this.dataset.errorUpload, 'error');
          this.fileInput.focus();
          return;
        }
        this.setStatus(this.dataset.sending, 'busy');
      }

      let posted = false;
      try {
        const response = await fetch(this.form.action, {
          method: 'POST',
          headers: { Accept: 'text/html' },
          body: new URLSearchParams(new FormData(this.form)),
        });
        posted = response.ok && new URL(response.url).searchParams.has(POSTED_PARAM);
      } catch {
        posted = false;
      }

      if (posted) {
        submittedForm.write(null);
        this.setBusy(false);
        this.setStatus('', '');
        this.showSuccess();
        return;
      }
      // Shopify n'a pas confirmé l'envoi (défi anti-spam à afficher, champ refusé, réseau) :
      // le formulaire repart par un envoi classique, qui recharge la page et affiche la réponse.
      HTMLFormElement.prototype.submit.call(this.form);
    }

    async upload(file) {
      const body = new FormData();
      body.append('id', this.dataset.carrierVariant);
      body.append('quantity', this.dataset.carrierQuantity);
      body.append(`properties[${FILE_PROPERTY}]`, file);

      const line = await this.cartRequest(this.dataset.cartAddUrl, { body });
      this.removeLine(line.key);

      const url = line.properties?.[FILE_PROPERTY];
      if (!url) throw new Error('upload sans url');
      return url;
    }

    // La ligne est notée avant son retrait, que personne n'attend : s'il échoue ou si la page se ferme avant,
    // il est retenté au prochain affichage du formulaire.
    async removeLine(key) {
      pendingLines.add(key);
      try {
        await this.cartRequest(this.dataset.cartChangeUrl, {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
      } catch {
        return;
      }
      pendingLines.delete(key);
    }

    // Le suivi d'audience de la boutique observe window.fetch pour compter les ajouts au panier.
    // Ce transit n'en est pas un : il passe par un contexte vierge pour ne pas fausser les statistiques.
    async cartRequest(path, { headers, body }) {
      const frame = document.createElement('iframe');
      frame.hidden = true;
      document.body.append(frame);
      try {
        const response = await frame.contentWindow.fetch(new URL(`${path}.js`, window.location.origin), {
          method: 'POST',
          headers: { Accept: 'application/json', ...headers },
          body,
        });
        if (!response.ok) throw new Error(`panier ${response.status}`);
        return await response.json();
      } finally {
        frame.remove();
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.submitButton.disabled = busy;
      this.form.setAttribute('aria-busy', String(busy));
      if (!this.fileInput) return;
      this.fileInput.disabled = busy;
      this.removeButton.disabled = busy;
    }

    setStatus(message, state) {
      this.status.textContent = message;
      this.status.dataset.state = state;
    }

    showSuccess() {
      this.body.hidden = true;
      this.success.hidden = false;
      this.success.focus();
      this.trackLead();
    }

    trackLead() {
      const send = () => {
        if (typeof window.gtag !== 'function') return;
        window.gtag('event', 'generate_lead', {
          form_name: this.dataset.formName,
          language: this.dataset.locale,
        });
      };
      const url = new URL(window.location.href);
      url.searchParams.delete(POSTED_PARAM);
      window.history.replaceState(window.history.state, '', url);

      if (document.readyState === 'complete') send();
      else window.addEventListener('load', send, { once: true });
    }
  }

  customElements.define('request-form', RequestForm);
}
